import {ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
    ConfiguratorScenarioService,
    ScenarioDTO,
    ScenarioVersionLiteDTO,
    TransactionalScenarioProcess
} from '@tj/tj-dmp-lib-api';
import {SelectIconComponent} from '../../../components/select-icon/select-icon.component';
import {MessageService} from 'primeng/api';
import {UtilsService} from '../../../services/utils.service';
import {WorkflowEditDraftService} from '../../../services/workflow-edit-draft.service';

@Component({
    selector: 'dmp-workflow-edit',
    templateUrl: './workflow-edit.component.html',
    styleUrls: ['./workflow-edit.component.scss'],
    standalone: false
})
export class WorkflowEditComponent implements OnInit, OnChanges {
    public loading = false;

    @Input() disabled: boolean;
    @Input() workflow: ScenarioDTO;
    @Input() version: ScenarioVersionLiteDTO;
    @Input() latestVersion: number;
    @Input() publishedVersion: number | null;
    @Input() tenant: string;
    @Input() partner: string;

    @Output() workflowUpdated = new EventEmitter<ScenarioDTO>();

    @ViewChild(SelectIconComponent) iconSettings: SelectIconComponent;

    loaded = false;

    form: UntypedFormGroup;

    actualProcess: TransactionalScenarioProcess;

    get f(): { [key: string]: AbstractControl } {
        return this.form.controls;
    }

    get transactionalProcess(): TransactionalScenarioProcess | null {
        if (!this.workflow || !this.actualProcess) return null;
        return (this.workflow.type || '').toLowerCase() === 'transactional' ? this.actualProcess as TransactionalScenarioProcess : null;
    }

    // Un process déjà enregistré signifie qu'un premier step existe déjà (côté backend, process est
    // maintenant toujours synchronisé sur le type de ce step — voir ScenarioService côté configurator).
    // Le rendre à nouveau modifiable ici permettrait de changer le type du step de déclenchement sans
    // passer par le canvas, ce qui n'a pas de sens (le step existant resterait de l'ancien type).
    get isEntryPointLocked(): boolean {
        return !!this.transactionalProcess?.entryPoint;
    }

    // Agissent toujours sur publishedVersion (la version réellement active), qu'on soit en train de
    // regarder cette version-là ou un brouillon plus récent au-dessus (ex. v1 active, v2 brouillon) :
    // mettre en pause/arrêter v1 ne doit pas nécessiter de quitter l'édition de v2. Le libellé du
    // bouton (voir *ButtonLabel ci-dessous) précise alors quelle version est concernée.

    // Se mettre en pause n'a de sens que si le scénario tourne réellement — déjà en pause, "Mettre en
    // pause" n'a rien à faire là (c'est canShowResumePublished/onStartWorkflow qui prennent le relais).
    get canShowPause(): boolean {
        if (this.publishedVersion == null) {
            return false;
        }
        const scenario = (this.workflow?.statusScenario || '').toUpperCase();
        return scenario === 'IN_PROGRESS' || scenario === 'INPROGRESS';
    }

    // Arrêter (progressif/forcé) a du sens qu'on tourne ou qu'on soit déjà en pause.
    get canShowStop(): boolean {
        if (this.publishedVersion == null) {
            return false;
        }
        const scenario = (this.workflow?.statusScenario || '').toUpperCase();
        return scenario === 'IN_PROGRESS' || scenario === 'INPROGRESS' || scenario === 'ON_PAUSE' || scenario === 'ONPAUSE';
    }

    // " (v.X)" seulement quand la version affichée n'est pas celle que le bouton contrôle (cas d'un
    // brouillon au-dessus de la version active) : la lecture reste identique à avant dans le cas
    // simple (version affichée = version active).
    private pauseStopVersionSuffix(): string {
        if (this.publishedVersion != null && this.version?.version !== this.publishedVersion) {
            return ` (v.${this.publishedVersion})`;
        }
        return '';
    }

    getPauseButtonLabel(): string {
        return this.translate.instant('workflow.edit.button.pause') + this.pauseStopVersionSuffix();
    }

    getProgressiveStopButtonLabel(): string {
        return this.translate.instant('workflow.edit.button.progressiveStop') + this.pauseStopVersionSuffix();
    }

    getForceStopButtonLabel(): string {
        return this.translate.instant('workflow.edit.button.forceStop') + this.pauseStopVersionSuffix();
    }

    get canShowStart(): boolean {
        return this.version?.version === this.latestVersion
            && this.version?.status !== ScenarioVersionLiteDTO.StatusEnum.PUBLISH;
    }

    // Relancer publishedVersion (mise en pause/arrêtée) depuis n'importe quelle page — symétrique à
    // canShowPauseStop. Exclut le cas où la page affichée EST déjà publishedVersion : canShowStart
    // le couvre alors déjà (même version), pas besoin des deux boutons.
    get canShowResumePublished(): boolean {
        if (this.publishedVersion == null || this.version?.version === this.publishedVersion) {
            return false;
        }
        const scenario = (this.workflow?.statusScenario || '').toUpperCase();
        return scenario === 'ON_PAUSE' || scenario === 'ONPAUSE' || scenario === 'STOPPED';
    }

    getResumeButtonLabel(): string {
        return this.translate.instant('workflow.edit.button.start', { version: this.publishedVersion });
    }

    getEntryPointLabel(entryPoint: string): string {
        if (!entryPoint) return '';
        return this.translate.instant(`components.select-entryPoint.entryPoint.${entryPoint}`);
    }

    getStartButtonLabel(): string {
        return this.translate.instant('workflow.edit.button.start', { version: this.version?.version });
    }

    constructor(
        private readonly translate: TranslateService,
        private readonly configuratorScenarioService: ConfiguratorScenarioService,
        private readonly messageService: MessageService,
        private readonly fb: UntypedFormBuilder,
        private readonly cdr: ChangeDetectorRef,
        private readonly utilsService: UtilsService,
        private readonly draftService: WorkflowEditDraftService
    ) { }

    ngOnInit(): void {
        this.initializeComponent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.loaded) {
            if (changes['version'] || changes['workflow']) {
                this.loaded = false;
                this.initializeComponent();
            }
            if (changes['disabled']) {
                if (this.form) {
                    this.updateFormDisabledState();
                    if (!this.disabled) {
                        this.patchProcessFields();
                    }
                }
            }
            if (changes['workflow'] && this.form && !changes['version']) {
                this.patchProcessFields();
            }
        }
    }

    private initializeComponent(): void {
        if (this.workflow) {
            this.getActualProcess();
        }
        this.initializeForm();
    }

    private initializeForm(): void {
        // Restaure les champs pas encore enregistrés si l'utilisateur est allé éditer les étapes
        // (canvas) sans cliquer "Enregistrer" ici : cette navigation détruit/recrée ce composant, la
        // saisie serait sinon perdue. N'a de sens qu'en édition active.
        const draft = !this.disabled
            ? this.draftService.get(this.workflow?.scenarioId, this.version?.version)
            : null;

        let formConfig: any = {
            name: [{ value: draft?.name ?? this.workflow?.name ?? null, disabled: this.disabled }, [Validators.required, Validators.maxLength(50)]],
            description: [{ value: draft?.description ?? this.workflow?.description ?? null, disabled: this.disabled }, Validators.maxLength(255)],
            icon: [{value: draft?.icon ?? this.workflow?.icon ?? null, disabled: this.disabled}],
            testEmails: [{value: draft?.testEmails ?? this.workflow?.testEmails ?? null, disabled: this.disabled}],
            allowMultipleEnrollments: [{value: draft?.allowMultipleEnrollments ?? this.workflow?.allowMultipleEnrollments ?? false, disabled: this.disabled}],
            recordSaleCost: [{value: draft?.recordSaleCost ?? this.workflow?.recordSaleCost ?? true, disabled: this.disabled}]
        };

        if (this.workflow?.type === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
            formConfig = {
                ...formConfig,
                entryPoint: [{ value: draft?.entryPoint ?? this.transactionalProcess?.entryPoint ?? null, disabled: this.disabled }, Validators.required]
            };
        }

        this.form = this.fb.group(formConfig);
        this.loaded = true;
    }

    private updateFormDisabledState(): void {
        if (!this.form) return;

        Object.keys(this.form.controls).forEach(key => {
            const control = this.form.get(key);
            if (control) {
                if (this.disabled) {
                    control.disable();
                } else {
                    control.enable({ emitEvent: false });
                }
            }
        });
        this.form.updateValueAndValidity();
    }

    private getActualProcess(): void {
        const process = this.workflow?.process ?? this.version?.process;
        this.actualProcess = null;
        if (this.workflow.type === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
            this.actualProcess = process as TransactionalScenarioProcess;
        }
    }

    public getFormValue() {
        return this.form.getRawValue();
    }

    public isFormValid(): boolean {
        if (!this.form) {
            return false;
        }
        if (this.disabled) {
            return true;
        }
        this.patchProcessFields();
        this.form.updateValueAndValidity();
        return !this.hasInvalidEnabledControls();
    }

    private hasInvalidEnabledControls(): boolean {
        return Object.keys(this.form.controls).some(key => {
            const control = this.form.get(key);
            return control && !control.disabled && control.invalid;
        });
    }

    private patchProcessFields(): void {
        if (!this.form || !this.workflow) {
            return;
        }
        this.getActualProcess();
        if (this.workflow.type === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
            const entryPoint = this.transactionalProcess?.entryPoint;
            const ctrl = this.form.get('entryPoint');
            if (entryPoint && ctrl && !ctrl.value) {
                ctrl.setValue(entryPoint, { emitEvent: false });
                ctrl.updateValueAndValidity();
            }
        }
    }

    public markFormTouched(): void {
        Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
    }

    onStartWorkflow(): void {
        if (!this.tenant || !this.partner || !this.workflow || !this.version) return;
        this.loading = true;
        // Optimistically update local status so the button hides immediately
        // this.updateLocalStatus(ScenarioDTO.StatusScenarioEnum.INPROGRESS); // désactivé : statuts STARTED/PAUSED ne correspondent pas à l'API (PUBLISH/STOPPED)
        this.cdr.markForCheck();
        this.configuratorScenarioService.updateStatus(this.tenant, this.partner, this.workflow.scenarioId, this.version.version, ScenarioDTO.StatusScenarioEnum.INPROGRESS).subscribe({
            next: () => {
                this.reloadScenarioVersion();
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('workflow.message.error'), detail: e?.message || e?.toString() });
            }
        });
    }

    onResumePublished(): void {
        if (!this.tenant || !this.partner || !this.workflow || this.publishedVersion == null) return;
        this.loading = true;
        this.cdr.markForCheck();
        this.configuratorScenarioService.updateStatus(this.tenant, this.partner, this.workflow.scenarioId, this.publishedVersion, ScenarioDTO.StatusScenarioEnum.INPROGRESS).subscribe({
            next: () => {
                this.reloadScenarioVersion();
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('workflow.message.error'), detail: e?.message || e?.toString() });
            }
        });
    }

    onPauseWorkflow(): void {
        // Agit sur publishedVersion (voir canShowPauseStop) : la version affichée peut être un
        // brouillon plus récent que celle réellement active.
        if (!this.tenant || !this.partner || !this.workflow || !this.version || this.publishedVersion == null) return;
        this.loading = true;
        // this.updateLocalStatus(ScenarioDTO.StatusScenarioEnum.ONPAUSE);
        this.cdr.markForCheck();
        this.configuratorScenarioService.updateStatus(this.tenant, this.partner, this.workflow.scenarioId, this.publishedVersion, ScenarioDTO.StatusScenarioEnum.ONPAUSE).subscribe({
            next: () => {
                this.reloadScenarioVersion();
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('workflow.message.error'), detail: e?.message || e?.toString() });
            }
        });
    }

    onProgressiveStopWorkflow(): void {
        // Agit sur publishedVersion (voir canShowPauseStop) : la version affichée peut être un
        // brouillon plus récent que celle réellement active.
        if (!this.tenant || !this.partner || !this.workflow || !this.version || this.publishedVersion == null) return;
        this.loading = true;
        // this.updateLocalStatus(ScenarioDTO.StatusScenarioEnum.STOPPED);
        this.cdr.markForCheck();
        this.configuratorScenarioService.updateStatus(this.tenant, this.partner, this.workflow.scenarioId, this.publishedVersion, ScenarioDTO.StatusScenarioEnum.STOPPED).subscribe({
            next: () => {
                this.reloadScenarioVersion();
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('workflow.message.error'), detail: e?.message || e?.toString() });
            }
        });
    }

    onForceStopWorkflow(): void {
        // Agit sur publishedVersion (voir canShowPauseStop) : la version affichée peut être un
        // brouillon plus récent que celle réellement active.
        if (!this.tenant || !this.partner || !this.workflow || !this.version || this.publishedVersion == null) return;
        this.loading = true;
        // this.updateLocalStatus(ScenarioDTO.StatusScenarioEnum.STOPPED);
        this.cdr.markForCheck();
        this.configuratorScenarioService.updateStatus(this.tenant, this.partner, this.workflow.scenarioId, this.publishedVersion, ScenarioDTO.StatusScenarioEnum.STOPPED).subscribe({
            next: () => {
                this.reloadScenarioVersion();
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: this.translate.instant('workflow.message.error'), detail: e?.message || e?.toString() });
            }
        });
    }
    /**
     * Reload scenario/version from backend after status change to ensure UI is always synced.
     */
    private reloadScenarioVersion(): void {
        this.configuratorScenarioService.getVersion(this.tenant, this.workflow.scenarioId, this.version.version).subscribe({
            next: (scenario) => {
                this.workflow = scenario;
                if ((scenario as any).versions && Array.isArray((scenario as any).versions)) {
                    const foundVersion = (scenario as any).versions.find((v: any) => v.version === this.version.version);
                    if (foundVersion) {
                        this.version = foundVersion as ScenarioVersionLiteDTO;
                    }
                }
                this.getActualProcess();
                // Only update form values, don't rebuild the form to avoid control mismatch errors
                if (this.form) {
                    const patch: Record<string, unknown> = {
                        name: this.workflow?.name ?? null,
                        description: this.workflow?.description ?? null,
                        icon: this.workflow?.icon ?? null
                    };
                    if (this.workflow?.type === ScenarioDTO.TypeEnum.TRANSACTIONAL) {
                        patch.entryPoint = this.transactionalProcess?.entryPoint ?? null;
                    }
                    this.form.patchValue(patch);
                }
                this.loading = false;
                this.workflowUpdated.emit(scenario);
            },
            error: (e) => {
                this.loading = false;
                this.messageService.add({ severity: 'warn', summary: this.translate.instant('workflow.message.warn'), detail: e?.message || e?.toString() });
            }
        });
    }

    /** Update local UI state after a status change so the buttons reflect the new state immediately */
    private updateLocalStatus(status: string): void {
        try {
            if (this.workflow) {
                this.workflow.statusScenario = status as ScenarioDTO.StatusScenarioEnum;
                this.workflow.statusScenarioChangedAt = new Date();
            }

            if (this.version) {
                switch (status) {
                    case ScenarioDTO.StatusScenarioEnum.INPROGRESS:
                        (this.version as any).status = 'STARTED';
                        break;
                    case ScenarioDTO.StatusScenarioEnum.ONPAUSE:
                        (this.version as any).status = 'PAUSED';
                        break;
                    case ScenarioDTO.StatusScenarioEnum.STOPPED:
                        (this.version as any).status = 'STOPPED';
                        break;
                    default:
                        break;
                }
            }
        } catch (err) {
            console.error('Error updating local status', err);
        }
    }

    updateIcon(icon: string) {
        this.form.patchValue({ icon });
    }
}
